import { Reducer } from "redux";

import { LibraryItem } from "@/Contracts/Users";
import {
	SavedLayoutsActionTypes,
	SavedLayoutsActions
} from "@/Actions/SavedLayouts";
import {
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata
} from "@/Contracts/UI";

export interface SavedLayoutsState {
	readonly isLoading: boolean;
	readonly layoutList: LibraryItem[];
	readonly layoutCount: number;
	readonly viewList: CustomViewResponse | null;
	readonly currentActiveView: CustomView | null;
	readonly viewMetadata: CustomViewsMetadata | null;
	readonly isViewChanged: boolean;
}

export const initialSavedLayoutsState: SavedLayoutsState = {
	isLoading: false,
	layoutList: [],
	layoutCount: 0,
	viewList: null,
	currentActiveView: null,
	viewMetadata: null,
	isViewChanged: false
};

export const SavedLayoutsReducer: Reducer<
	SavedLayoutsState,
	SavedLayoutsActions
> = (
	state: SavedLayoutsState = initialSavedLayoutsState,
	action: SavedLayoutsActions
): SavedLayoutsState => {
	switch (action.type) {
		case SavedLayoutsActionTypes.SHOW_SAVED_LAYOUTS_LOADER:
			return { ...state, isLoading: true };

		case SavedLayoutsActionTypes.HIDE_SAVED_LAYOUTS_LOADER:
			return { ...state, isLoading: false };

		case SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_REQUESTED:
			return { ...state, isLoading: true };

		case SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_FAILED:
			return { ...state, isLoading: false };

		case SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_SUCCESSFUL:
			return {
				...state,
				isLoading: false,
				layoutList: action.layoutList.data,
				layoutCount:
					action.layoutList.filtered_count_map?.all ??
					state.layoutCount
			};

		case SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_VIEWS_SUCCESSFUL:
			return { ...state, viewList: action.viewList };

		case SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_VIEW_METADATA_SUCCESSFUL:
			return { ...state, viewMetadata: action.metadata };

		case SavedLayoutsActionTypes.CREATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL:
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: [...(state.viewList?.data ?? []), action.view]
					  }
					: state.viewList,
				isViewChanged: false
			};

		case SavedLayoutsActionTypes.UPDATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL:
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.map(view =>
								view.id === action.view.id ? action.view : view
							)
					  }
					: state.viewList,
				isViewChanged: false
			};

		case SavedLayoutsActionTypes.DELETE_SAVED_LAYOUTS_VIEW_SUCCESSFUL:
			return {
				...state,
				currentActiveView: action.initialView,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.filter(
								view => view.id !== action.deletedView.id
							)
					  }
					: state.viewList,
				isViewChanged: false
			};

		case SavedLayoutsActionTypes.UPDATE_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW:
			return {
				...state,
				currentActiveView: action.currentActiveView,
				isViewChanged: action.isViewChanged
			};

		case SavedLayoutsActionTypes.RESET_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW:
			return {
				...state,
				isLoading: false,
				currentActiveView: null,
				isViewChanged: false,
				viewList: null,
				viewMetadata: null,
				layoutList: [],
				layoutCount: 0
			};

		default:
			return state;
	}
};
